import { useCallback, useMemo, useRef, useState } from 'react';
import type { FocusEvent, ComponentProps } from 'react';
import { I18nLegacy } from '@silvertours/front-legacy-shared';
import { Ui } from '@silvertours/front-shared';
import {
  SearchSuggestion,
  useSearchSuggestions,
  getLocation,
  Runtime,
} from '@silvertours/front-entities';
import { LocationSuggest } from '../LocationSuggest';
import { SearchHistory } from '../SearchHistory/SearchHistory';

import { Container } from './SearchInput.styles';
import { useSearchContext } from '../context';
import type { RentalDirection } from '../context/types';

type CommonSearchInputProps = {
  direction: RentalDirection;
  ariaErrorMessage?: string;
  // Clarity unmask (LPOT-1716): opt-in per caller, since SearchInput is also
  // used by WidgetSearchForm, which must stay masked.
  unmaskForClarity?: boolean;
} & Pick<
  ComponentProps<typeof Ui.Input>,
  | 'id'
  | 'name'
  | 'label'
  | 'clearButtonLabel'
  | 'placeholder'
  | 'icon'
  | 'cleanable'
  | 'className'
>;

type DepartureProps = {
  direction: 'departure';
  onServiceNumbersSubmit: () => void;
} & CommonSearchInputProps;

type DestinationProps = {
  direction: 'destination';
} & CommonSearchInputProps;

type SearchInputProps = DepartureProps | DestinationProps;

const isDeparture = (props: SearchInputProps): props is DepartureProps =>
  (props as DepartureProps).direction === 'departure';

const getSubmit = (props: SearchInputProps) =>
  isDeparture(props) ? props.onServiceNumbersSubmit : undefined;

const SearchInput = (props: SearchInputProps) => {
  const {
    getInputValue,
    isInternalCall,
    state,
    setInputValue,
    makeSetLocation,
    setError,
  } = useSearchContext();
  const {
    direction,
    icon,
    label,
    clearButtonLabel,
    placeholder,
    id,
    name,
    cleanable,
    className,
    ariaErrorMessage,
    unmaskForClarity,
  } = props;
  const handleSubmit = getSubmit(props);
  const setLocation = makeSetLocation(direction);
  const value = getInputValue(direction);
  const shouldSearchServiceNumbers =
    isInternalCall && direction === 'departure' && value.startsWith('#');

  const [showSearchHistory, setShowSearchHistory] = useState(false);
  const [showLocationSuggest, setShowLocationSuggest] = useState(false);
  const [activeDescendant, setActiveDescendant] = useState<string | undefined>(
    undefined,
  );
  const fieldRef = useRef<HTMLInputElement>(null);

  const { language, market } = I18nLegacy.useLocale();
  const { consumerApiSettings } = Runtime.useConsumerApi();

  const { clearResults, fetchSuggestions, loading, results } =
    useSearchSuggestions(language, market, consumerApiSettings);

  const isExpanded = useMemo(
    () => showLocationSuggest || showSearchHistory,
    [showLocationSuggest, showSearchHistory],
  );

  const openedBox = useMemo(
    () => (showSearchHistory ? 'searchHistory' : 'locationSuggest'),
    [showSearchHistory],
  );

  const handleChange = useCallback(
    (inputValue: string) => {
      setInputValue(direction, inputValue);

      if (inputValue === '') {
        clearResults();
        setShowSearchHistory(true);
        setShowLocationSuggest(false);
        setLocation(null);
      } else {
        setShowSearchHistory(false);
        if (shouldSearchServiceNumbers) {
          setShowLocationSuggest(false);
        } else {
          setShowLocationSuggest(true);
          setLocation(null);
          fetchSuggestions(inputValue);
        }
      }
    },
    [
      direction,
      setInputValue,
      clearResults,
      setShowSearchHistory,
      setShowLocationSuggest,
      setLocation,
      fetchSuggestions,
      shouldSearchServiceNumbers,
    ],
  );

  const handleSuggestionValue = useCallback(
    (suggestion?: SearchSuggestion) => {
      if (suggestion) {
        const location = getLocation(suggestion);
        setShowLocationSuggest(false);
        setInputValue(direction, location.name);
        setLocation(location);
        clearResults();
      } else {
        handleChange(value);
        if (shouldSearchServiceNumbers && !!handleSubmit) {
          handleSubmit();
        }
      }
    },
    [
      direction,
      setInputValue,
      setLocation,
      clearResults,
      handleChange,
      value,
      shouldSearchServiceNumbers,
      handleSubmit,
    ],
  );

  const handleFocus = useCallback(
    (event: FocusEvent<HTMLInputElement>) => {
      setError({ kind: 'locationError', message: '' });
      if (event.target.value === '') {
        setShowSearchHistory(true);
      } else {
        fetchSuggestions(event.target.value);
        setShowLocationSuggest(true);
      }
    },
    [setError, fetchSuggestions, setShowSearchHistory, setShowLocationSuggest],
  );

  const handleBlur = useCallback((event: FocusEvent<HTMLInputElement>) => {
    if (!event.relatedTarget?.className.includes('searchHistory')) {
      setShowSearchHistory(false);
    }
    if (!(
      event.relatedTarget?.className.includes('inputCloseIcon') ||
      event.relatedTarget?.id.includes('locationSuggest')
    )) {
      setShowLocationSuggest(false);
    }
  }, []);

  if (!state) {
    return null;
  }

  const isInvalid = () => {
    if (!state.locationError) {
      return false;
    }
    if (direction === 'departure') {
      return !state.depLocation;
    }
    return !state.destLocation;
  };

  return (
    <Container className={className}>
      <Ui.Input
        // Clarity unmask (LPOT-1716): input content is auto-masked in every
        // masking mode, so the container-level unmask alone does not reach it —
        // the attribute must sit on the field itself.
        data-clarity-unmask={unmaskForClarity ? 'true' : undefined}
        type="text"
        ref={fieldRef}
        value={value}
        label={label}
        clearButtonLabel={clearButtonLabel}
        name={name ?? id}
        id={id}
        placeholder={placeholder}
        icon={icon}
        iconPosition="left"
        cleanable={cleanable}
        invalid={isInvalid()}
        error={!ariaErrorMessage ? state.locationError : undefined}
        autoComplete="off"
        forcedLabel
        onFocus={handleFocus}
        onBlur={handleBlur}
        onChange={event => handleChange(event.target.value)}
        role="combobox"
        aria-errormessage={
          ariaErrorMessage && isInvalid() ? ariaErrorMessage : undefined
        }
        aria-haspopup="grid"
        aria-controls={openedBox}
        aria-autocomplete={openedBox === 'searchHistory' ? 'none' : 'list'}
        aria-expanded={isExpanded ? 'true' : 'false'}
        aria-activedescendant={isExpanded ? activeDescendant : undefined}
      />
      {showSearchHistory && (
        <SearchHistory
          onItemClick={clearResults}
          onLastElementBlur={() => {
            setShowSearchHistory(false);
          }}
          onItemSelect={setActiveDescendant}
        />
      )}

      {showLocationSuggest && (
        <LocationSuggest
          connectedInputContainer={fieldRef}
          loading={loading}
          onLastElementBlur={() => {
            setShowLocationSuggest(false);
          }}
          onItemClick={handleSuggestionValue}
          onItemSelect={setActiveDescendant}
          results={results}
          direction={direction}
        />
      )}
    </Container>
  );
};

export { SearchInput };
